import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tile,
  Button,
  TextInput,
  InlineNotification,
  Loading,
  Modal,
} from '@carbon/react';
import { Api, Copy, TrashCan, CheckmarkFilled, Document } from '@carbon/icons-react';
import {
  listAccountApiKeys,
  createAccountApiKey,
  revokeAccountApiKey,
} from '../../services/accountApiKeysService';

const ApiKeysSettings = () => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokeId, setRevokeId] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listAccountApiKeys();
      setKeys(data.keys || []);
    } catch (err) {
      setError(err.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError('');
      const created = await createAccountApiKey({ name: name.trim() || 'API key' });
      setNewKey(created.key);
      setName('');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!newKey) return;
    try {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const handleRevoke = async () => {
    if (!revokeId) return;
    try {
      setRevoking(true);
      await revokeAccountApiKey(revokeId);
      setRevokeId(null);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to revoke API key');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <>
      <Tile className="settings-page__card">
        <div className="settings-page__card-header">
          <Api size={20} />
          <h3>API keys</h3>
        </div>
        <div className="settings-page__card-content">
          <p className="settings-page__description">
            Generate a key for this account to read sensors, metrics, GPS, shipments and reports
            over HTTPS. Keys are shown once. Use{' '}
            <code>Authorization: Bearer cv_live_…</code> or <code>X-API-Key</code>.
          </p>
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Document}
            onClick={() => navigate('/dashboard/developers')}
            style={{ marginBottom: '16px' }}
          >
            Open API documentation
          </Button>

          {error && (
            <InlineNotification
              kind="error"
              title="API keys"
              subtitle={error}
              onClose={() => setError('')}
              lowContrast
              style={{ marginBottom: '16px' }}
            />
          )}

          {newKey && (
            <InlineNotification
              kind="success"
              title="Copy this key now"
              subtitle={newKey}
              hideCloseButton
              lowContrast
              style={{ marginBottom: '12px' }}
            />
          )}
          {newKey && (
            <Button
              kind="secondary"
              size="sm"
              renderIcon={copied ? CheckmarkFilled : Copy}
              onClick={handleCopy}
              style={{ marginBottom: '16px' }}
            >
              {copied ? 'Copied' : 'Copy key'}
            </Button>
          )}

          <div className="settings-page__field">
            <TextInput
              id="api-key-name"
              labelText="Key name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Warehouse integration"
            />
          </div>
          <Button kind="primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Generate API key'}
          </Button>

          <div style={{ marginTop: '24px' }}>
            {loading ? (
              <Loading small withOverlay={false} description="Loading keys" />
            ) : keys.length === 0 ? (
              <p className="settings-page__description">No active keys yet.</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {keys.map((key) => (
                  <li
                    key={key.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '12px 0',
                      borderTop: '1px solid var(--settings-border, #e0e0e0)',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{key.name}</div>
                      <div className="settings-page__description" style={{ margin: 0 }}>
                        {key.key_prefix}… · created {new Date(key.created_at).toLocaleDateString()}
                        {key.last_used_at
                          ? ` · last used ${new Date(key.last_used_at).toLocaleString()}`
                          : ' · never used'}
                      </div>
                    </div>
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      hasIconOnly
                      iconDescription="Revoke"
                      renderIcon={TrashCan}
                      onClick={() => setRevokeId(key.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Tile>

      <Modal
        open={Boolean(revokeId)}
        modalHeading="Revoke this API key?"
        primaryButtonText={revoking ? 'Revoking…' : 'Revoke'}
        secondaryButtonText="Cancel"
        danger
        primaryButtonDisabled={revoking}
        onRequestClose={() => setRevokeId(null)}
        onRequestSubmit={handleRevoke}
      >
        <p>Integrations using this key will stop immediately. You cannot recover the key.</p>
      </Modal>
    </>
  );
};

export default ApiKeysSettings;
